import {useState} from 'react'
import type React from 'react'

type NotFunction<T> = T extends (...args: unknown[]) => unknown ? never : T

/**
 * State that stays in sync with a prop/external value.
 *
 * When `initialValue` changes (compared via `isEqual`), the state resets to match.
 * This is the "primitives in state" pattern from React docs:
 * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 *
 * @param nextValue - The value to sync from (must not be a function)
 * @param opts.isPropUpdateDisabled - Skip syncing (e.g., while a form is dirty)
 * @param opts.isEqual - Comparison function, defaults to Object.is
 */
export function useSyncedState<T>(
  nextValue: NotFunction<T>,
  {isPropUpdateDisabled = false, isEqual = Object.is} = {},
): readonly [T, React.Dispatch<React.SetStateAction<T>>] {
  const [currentValue, setCurrentValue] = useState<T>(nextValue)
  const [previousValue, setPreviousValue] = useState<T>(nextValue)

  /**
   * This is _not_ done in effect, but instead during render.
   *
   * This is safe because it's in the same component. React will immediately queue an update, and
   * avoid the work in the render pass, which saves a potentially large render cycle that would
   * get thrown away immediately
   */
  if (!isPropUpdateDisabled && !isEqual(previousValue, nextValue)) {
    setPreviousValue(nextValue)
    setCurrentValue(nextValue)
    return [nextValue, setCurrentValue] as const
  }

  return [currentValue, setCurrentValue] as const
}
